import { memo } from 'react';
import { HelpCircle } from 'lucide-react';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
interface FAQItem {
  id: string;
  question: string;
  answer: string;
}
const faqItems: FAQItem[] = [{
  id: 'duracion',
  question: '¿Cuánto dura una consulta?',
  answer: 'La primera consulta tiene una duración aproximada de 60 minutos, ya que incluye una evaluación completa de tu situación. Las consultas de seguimiento duran entre 45 y 60 minutos, tiempo suficiente para revisar tu progreso y ajustar el plan según tus necesidades.'
}, {
  id: 'frecuencia',
  question: '¿Con qué frecuencia debo tener consultas de seguimiento?',
  answer: 'Normalmente recomiendo consultas cada 2-4 semanas, dependiendo de tus objetivos y necesidades específicas. Esta frecuencia permite hacer un seguimiento adecuado de tu progreso y realizar los ajustes necesarios en tu plan nutricional.'
}, {
  id: 'online',
  question: '¿Las consultas online son igual de efectivas que las presenciales?',
  answer: 'Sí, las consultas online incluyen exactamente los mismos servicios que las presenciales. Realizamos la sesión por videollamada y tienes acceso al mismo seguimiento personalizado, plan nutricional y soporte continuo.'
}, {
  id: 'hambre',
  question: '¿Voy a pasar hambre con el plan nutricional?',
  answer: 'No, para nada. Mis planes nutricionales están diseñados para ser equilibrados y satisfactorios. No trabajo con dietas restrictivas ni conteo obsesivo de calorías. El objetivo es que aprendas a comer bien, disfrutando de la comida sin pasar hambre.'
}, {
  id: 'resultados',
  question: '¿En cuánto tiempo veré resultados?',
  answer: 'Las primeras mejoras suelen notarse entre las 3 y 4 primeras semanas: más energía, mejor digestión, mejor descanso... Los cambios físicos y de composición corporal son progresivos y sostenibles a largo plazo, porque trabajamos creando hábitos duraderos.'
}, {
  id: 'personalizado',
  question: '¿El plan nutricional se adapta a mis gustos y estilo de vida?',
  answer: 'Absolutamente. El plan está 100% personalizado según tus preferencias alimentarias, horarios, ritmo de vida y objetivos. No tiene sentido crear un plan que no puedas seguir. Trabajamos juntos para encontrar opciones que encajen en tu día a día.'
}, {
  id: 'primera-consulta',
  question: '¿Qué incluye la primera consulta?',
  answer: 'La primera consulta incluye una evaluación completa de tu historial de salud, hábitos alimentarios actuales, análisis de composición corporal, definición de objetivos realistas y la creación de tu plan nutricional inicial personalizado.'
}, {
  id: 'bono',
  question: '¿Merece la pena el bono de 3 sesiones?',
  answer: 'El bono de 3 sesiones de seguimiento supone un ahorro de 15€ respecto a contratar las sesiones por separado. Es ideal si buscas un seguimiento continuo, que es la forma más efectiva de lograr resultados duraderos y consolidar buenos hábitos.'
}];
const FAQ = memo(() => {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  return <section id="faq" className="section-padding bg-background">
      <div className="container-custom max-w-6xl">
        {/* Section Header */}
        <div ref={ref} className={`text-center max-w-2xl mx-auto mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-primary font-medium mb-4">
            FAQs
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Preguntas{' '}
            <span className="text-gradient">frecuentes</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Resuelvo las dudas más comunes sobre mis servicios de nutrición.
          </p>
        </div>

        {/* FAQ Cards Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {faqItems.map((item, index) => <Accordion key={item.id} type="single" collapsible className="w-full">
              <AccordionItem value={item.id} className="glass-card border-0 overflow-hidden">
                <AccordionTrigger className="text-left text-foreground hover:text-primary hover:no-underline px-6 py-5 font-medium">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-rose-light rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <HelpCircle className="w-4 h-4 text-rose-dark" aria-hidden="true" />
                    </div>
                    <span className="pr-4">{item.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed px-6 pb-5 pl-[4.25rem]">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            </Accordion>)}
        </div>
      </div>
    </section>;
});
FAQ.displayName = 'FAQ';
export default FAQ;